
// Session expired alert styles
const SESSION_ALERT_STYLES = `
  .session-expired-alert {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    background-color: #8a0f1c;
    color: white;
    padding: 16px 20px;
    text-align: center;
    z-index: 9999;
    box-shadow: 0 4px 12px rgba(0,0,0,0.2);
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    animation: slideDown 0.3s ease-out;
  }
  
  @keyframes slideDown {
    from {
      transform: translateY(-100%);
    }
    to {
      transform: translateY(0);
    }
  }
  
  .session-expired-alert a {
    color: white;
    text-decoration: underline;
    font-weight: bold;
  }
  
  .session-expired-alert a:hover {
    color: #ffeb3b;
  }
  
  .session-expired-alert button {
    float: right;
    background: transparent;
    border: none;
    color: white;
    cursor: pointer;
    font-size: 20px;
    margin-left: 16px;
    padding: 0 8px;
  }
  
  .session-expired-alert button:hover {
    color: #ffeb3b;
  }
`;

// Inject styles once
if (typeof document !== 'undefined' && !document.getElementById('session-alert-styles')) {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'session-alert-styles';
  styleSheet.innerText = SESSION_ALERT_STYLES;
  document.head.appendChild(styleSheet);
}

let alertTimeout = null;
let currentAlert = null;

export function showSessionExpiredAlert() {
  // Don't show duplicate alerts
  if (currentAlert && document.body.contains(currentAlert)) {
    return;
  }
  
  // Clear existing timeout
  if (alertTimeout) {
    clearTimeout(alertTimeout);
  }
  
  // Create alert - NO AUTO REFRESH
  const alertDiv = document.createElement('div');
  alertDiv.className = 'session-expired-alert';
  alertDiv.innerHTML = `
    <button type="button" aria-label="Dismiss session alert">&times;</button>
    Your session has expired or the request was not authorized. Please log in again.
  `;
   
  document.body.insertAdjacentElement('afterbegin', alertDiv);
  currentAlert = alertDiv;
  
  // Remove alert after 30 seconds (but NO auto refresh)
  alertTimeout = setTimeout(() => {
    if (alertDiv && document.body.contains(alertDiv)) {
      alertDiv.remove();
      currentAlert = null;
    }
  }, 5000);
  
  // Remove alert on dismiss button click
  const dismissButton = alertDiv.querySelector('button');
  if (dismissButton) {
    dismissButton.onclick = () => {
      if (alertDiv && document.body.contains(alertDiv)) {
        alertDiv.remove();
        currentAlert = null;
      }
      if (alertTimeout) clearTimeout(alertTimeout);
    };
  }
}

// Fix fetch interceptor to NOT refresh on 401
export function setupSessionInterceptors() {
  const originalFetch = window.fetch;
  window.fetch = async function(...args) {
    let url = args[0];

    if (typeof url === 'string') {
      // ✅ If the URL already has /backend-api/, don't normalize it
      if (url.includes('/backend-api/')) {
        console.log('[BACKEND TRACE] skipping normalization for backend-api URL:', url);
        // Skip normalization entirely
      } else {
        // Normalize non-backend-api URLs
        let normalizedUrl = url.replace(/^api\//, '/api/');

        try {
          const resolvedApiUrl = new URL(normalizedUrl, window.location.href);
          const apiPathIndex = resolvedApiUrl.pathname.indexOf('/api/');

          if (
            resolvedApiUrl.origin === window.location.origin &&
            apiPathIndex > 0
          ) {
            normalizedUrl = `${resolvedApiUrl.pathname.slice(apiPathIndex)}${resolvedApiUrl.search}${resolvedApiUrl.hash}`;
          }
        } catch (error) {
          console.warn('[BACKEND TRACE] unable to normalize API URL', {
            url,
            error
          });
        }

        if (normalizedUrl !== url) {
          console.warn('[BACKEND TRACE] normalizing route-relative API URL', {
            from: url,
            to: normalizedUrl,
            pageHref: window.location.href
          });
          url = normalizedUrl;
          args[0] = normalizedUrl;
        }
      }
    }

    const resolvedUrl = typeof url === 'string'
      ? new URL(url, window.location.href).href
      : url?.url;
    const isApiRequest = typeof url === 'string' && 
                         (url.includes('/api/') || url.includes('/dhis2/'));
    
    try {
      if (isApiRequest && process.env.NODE_ENV !== 'production') {
        console.log('[BACKEND TRACE] fetch request', {
          inputUrl: url,
          resolvedUrl,
          method: args[1]?.method || 'GET',
          pageHref: window.location.href
        });
      }

      const response = await originalFetch(...args);

      if (isApiRequest && process.env.NODE_ENV !== 'production') {
        console.log('[BACKEND TRACE] fetch response', {
          inputUrl: url,
          resolvedUrl,
          status: response.status,
          ok: response.ok,
          redirected: response.redirected,
          responseUrl: response.url
        });
      }
      
      // Only show alert for API requests, but DON'T refresh
      if (response.status === 401 && isApiRequest) {
        console.warn('⚠️ API session expired - showing alert');
        showSessionExpiredAlert();
      }
      
      return response;
    } catch (error) {
      if (isApiRequest && process.env.NODE_ENV !== 'production') {
        console.error('[BACKEND TRACE] fetch failed', {
          inputUrl: url,
          resolvedUrl,
          error
        });
      }
      throw error;
    }
  };
}
